/**
 * `LivelloGuide` — guida contestuale alla scala dei Livelli (`e-1..e-5`).
 *
 * Rende un piccolo pulsante con l'icona "info" che apre una `Modal` con la
 * spiegazione di ciascun Livello (codice, etichetta e descrizione estesa). È
 * pensato per stare accanto al `LivelloSelect` sia nell'inline edit della
 * tabella skill sia nel wizard di assessment.
 *
 * La scala, le etichette e le descrizioni provengono da `shared/dto`
 * (`LIVELLO_VALUES`, `LIVELLO_LABELS`, `LIVELLO_DESCRIPTIONS`): il componente
 * non duplica alcun testo di dominio.
 */
import { useState, type KeyboardEvent, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import {
  LIVELLO_DESCRIPTIONS,
  LIVELLO_LABELS,
  LIVELLO_VALUES,
} from '../shared/dto';
import { Button, Icon, Modal } from './ui';

export interface LivelloGuideProps {
  /** Classe extra sul trigger (es. per l'allineamento nel picker o nell'header). */
  className?: string;
}

/**
 * Icona "info" + modale con la legenda dei Livelli. Lo stato di apertura è
 * interno: il chiamante deve solo posizionare il componente.
 *
 * Il trigger è uno `<span role="button">` (non un `<button>`) e la modale è
 * renderizzata in un **portale** su `document.body`, con gli eventi fermati:
 * così la guida può stare anche dentro l'header di una colonna `DataTable`
 * (già cliccabile per l'ordinamento) senza annidare button, senza ereditare i
 * vincoli di larghezza/clipping della cella e senza far scattare il sort.
 */
export function LivelloGuide({ className }: LivelloGuideProps) {
  const [open, setOpen] = useState(false);

  const triggerClasses = ['livello-guide__trigger', className ?? '']
    .filter(Boolean)
    .join(' ');

  const openGuide = (e: MouseEvent | KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') openGuide(e);
  };

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        className={triggerClasses}
        aria-label="Guida ai livelli"
        title="Guida ai livelli"
        onClick={openGuide}
        onKeyDown={onKeyDown}
      >
        <Icon name="info" size={18} />
      </span>

      {open &&
        createPortal(
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Modal
              open
              onClose={() => setOpen(false)}
              title="Guida ai livelli"
              footer={
                <Button type="button" variant="primary" onClick={() => setOpen(false)}>
                  Ho capito
                </Button>
              }
            >
              <p className="livello-guide__intro">
                La scala esprime il grado di autonomia e di impatto della risorsa,
                dal semplice svolgimento di attività guidate fino alla definizione
                della visione strategica.
              </p>
              <ul className="livello-guide__list">
                {LIVELLO_VALUES.map((livello) => (
                  <li key={livello} className="livello-guide__item">
                    <span className="livello-guide__badge">
                      <span className="livello-guide__code">{livello}</span>
                      <span className="livello-guide__label">{LIVELLO_LABELS[livello]}</span>
                    </span>
                    <span className="livello-guide__desc">
                      {LIVELLO_DESCRIPTIONS[livello]}
                    </span>
                  </li>
                ))}
              </ul>
            </Modal>
          </div>,
          document.body,
        )}
    </>
  );
}

export default LivelloGuide;
